import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";

import {
  ArrowButton,
  Checkbox,
  TableMetadata,
  useTable,
  ColumnSortEnum,
  Spinner,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import type { SchoolCandidateNewResidencesRequest } from "../../../queries";
import { useAppDispatch } from "../../../app/store";
import { ContentArea, ListTable, ToolbarTop } from "../../../components";
import { SaveResult, ValidationFailure, Selectable } from "../../../models";
import {
  SchoolCandidateSort,
  SchoolCandidateResidenceChange,
  SchoolCandidateResidenceChangeSearch,
} from "../../../models/SchoolCandidate";
import {
  useGetInstitutionListForUserQuery,
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../../queries/codeBookQuery";
import { schoolCandidateDetailUrl } from "../../../routes/routes";
import { setRedirectURL } from "../../../slices/applicationSlice";
import { TableUtils, ObjectUtils } from "../../../utils";
import { getAddressString } from "../../../utils/formatUtils";
import { getSchoolCandidateResidenceCellClassName } from "../utils";
import {
  SchoolCandidateAddressAccessFilters,
  SchoolCandidateAssignCard,
  type SchoolCandidateAssignCardHandle,
} from "../components";
import { useSchoolCandidateAddressAccess } from "./useSchoolCandidateAddressAccess";
import { DEFAULT_FILTERS, ROLES_DEFAULT_FILTERS } from "./constants";
import { useSchoolYearEditable, useBatchManagement } from "../hooks";
import { useAuth, useTableState } from "../../../hooks";

type SchoolCandidateAddressAccessFilterState =
  SchoolCandidateResidenceChangeSearch & {
    countyId?: number;
    batchId?: number | null;
  };

/**
 * Only Global and County level users.
 * @returns
 */
export function SchoolCandidateAddressAccess() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { currentRole } = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();

  const storageKey = "SchoolCandidateResidenceChangeSearchTableState";

  const { initialTableState, initialStoredFilters, saveState } =
    useTableState<SchoolCandidateAddressAccessFilterState>({
      storageKey,
      defaultSortBy: SchoolCandidateSort.FirstName,
      defaultOrder: ColumnSortEnum.ASC,
      defaultPageSize: 50,
      extractFilters: (state) => {
        return state?.unassignedSchoolCandidates !== undefined
          ? (state as SchoolCandidateAddressAccessFilterState)
          : undefined;
      },
      defaultFilters: {
        ...DEFAULT_FILTERS,
        schoolYearId: currentSchoolYear?.id ?? 0,
        countyId: undefined,
        batchId: null,
      },
    });

  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<
    SchoolCandidateResidenceChange & Selectable
  >();
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidateResidenceChange & Selectable>>(
      defaultTableData
    );
  const [currentSearchRequest, setCurrentSearchRequest] =
    useState<SchoolCandidateNewResidencesRequest | null>(null);
  const currentFilters = currentSearchRequest?.search;

  const assignCardRef = useRef<SchoolCandidateAssignCardHandle | null>(null);
  const handleSchoolCandidateCheckedChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      assignCardRef.current?.handleSchoolCandidateCheckedChange(event);
    },
    []
  );

  const [selectedSchoolYear, setSelectedSchoolYear] = useState<number>();
  //TABLE METADATA
  const tableMetadata: TableMetadata<
    SchoolCandidateResidenceChange & Selectable
  > = {
    rowKey: "schoolCandidateId",
    columns: [
      {
        columnKey: "select",
        header: "",
        accessor: (item) => {
          return (
            <Checkbox
              label={""}
              name={nameof<typeof item>("schoolCandidateId")}
              value={item.schoolCandidateId}
              checked={item.isSelected}
              onChange={handleSchoolCandidateCheckedChange}
            />
          );
        },
      },
      {
        columnKey: "submittedRequest",
        header: "Predani zahtjev",
        accessor: (item) => {
          return (
            <div>
              <div className="text-nowrap">
                {item.firstName} {item.lastName}
              </div>
              <div className="text-nowrap">OIB: {item.oib}</div>
            </div>
          );
        },
        sortBy: 1,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item) => {
          return (
            <div className="text-nowrap text-right">
              {hrDate(new Date(item.dateOfBirth))}
            </div>
          );
        },
        sortBy: 2,
      },
      {
        columnKey: "permanentAddress",
        header: "Prebivaliste/boraviste",
        accessor: (item) => {
          const currentAddressClassName =
            getSchoolCandidateResidenceCellClassName(item, "differentCounty");

          return (
            <div>
              {item.currentPermanentResidence && (
                <b className={currentAddressClassName}>
                  {getAddressString(item.currentPermanentResidence)}
                </b>
              )}
              {item.currentTemporaryResidence && (
                <div className={currentAddressClassName}>
                  {getAddressString(item.currentTemporaryResidence)}
                </div>
              )}
            </div>
          );
        },
        sortBy: 3,
      },
      {
        columnKey: "newAddress",
        header: "Novo prebivaliste/boraviste",
        accessor: (item) => {
          const newAddressClassName = getSchoolCandidateResidenceCellClassName(
            item,
            "newAddressApproved"
          );

          return (
            <div>
              {item.newPermanentResidence && (
                <b className={newAddressClassName}>
                  {getAddressString(item.newPermanentResidence)}
                </b>
              )}
              {item.newTemporaryResidence && (
                <div className={newAddressClassName}>
                  {getAddressString(item.newTemporaryResidence)}
                </div>
              )}
            </div>
          );
        },
        sortBy: 4,
      },
      {
        columnKey: "addressTaken",
        header: "Adresa preuzeta",
        accessor: (item) => {
          return (
            <div className="text-nowrap">
              {item.newAdressApproved ? t("common.yes") : t("common.no")}
            </div>
          );
        },
        sortBy: 5,
      },
      {
        columnKey: "institution",
        header: "Osnovna škola",
        accessor: (item) => {
          return (
            <div>
              {item.institutionName ||
                institutions?.find((v) => v.id === item.institutionId)?.name}
            </div>
          );
        },
        sortBy: 6,
      },
      {
        columnKey: "submittedRequest2",
        header: "Predan zahtjev",
        accessor: (item) => {
          return (
            <div className="text-nowrap">
              {item.hasRequest ? t("common.yes") : t("common.no")}
            </div>
          );
        },
        sortBy: 7,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (
            <ArrowButton
              ariaLabel={t("common.details")}
              onClick={() => handleDetailsClicked(item.schoolCandidateId)}
            />
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  /** The table metadata. The search is performed when the tableState changes (useEffect)
   * which is triggerd by a call to setFilters. Why are we doing this?
   */
  const [tableProps, tableState] =
    useTable<SchoolCandidateResidenceChangeSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });

  const { data: schoolYears } = useGetSchoolYearListQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();

  const {
    authCounties,
    schoolCandidateNewResidences,
    isSchoolCandidateNewResidencesFetching,
    handleSearchNewResidences,
    startSchoolCandidateBatchRequest,
    saveInstitutionAssignmentRequest,
    setInstitutionAssignmentRequest,
    downloadSchoolCandidateResidencesRequest,
  } = useSchoolCandidateAddressAccess();

  const [currentCountyId, setCurrentCountyId] = useState<number | undefined>(
    undefined
  );
  const batchManagement = useBatchManagement({
    selectedCountyId: currentCountyId,
    countyIds: authCounties?.map((county) => county.id) ?? [],
  });
  const { selectedBatchId } = batchManagement;

  const { isSchoolYearEditable, evaluateSchoolYearEditable } =
    useSchoolYearEditable({ schoolYears });
  const [errors, setErrors] = useState<ValidationFailure[]>();

  useEffect(() => {
    if (ROLES_DEFAULT_FILTERS.includes(currentRole?.role ?? 0))
      setCurrentCountyId(authCounties[0]?.id);
  }, [authCounties, currentRole?.role]);

  useEffect(() => {
    if (!initialStoredFilters?.countyId) {
      return;
    }

    handleCountyChange(initialStoredFilters.countyId);
  }, [initialStoredFilters?.countyId]);

  useEffect(() => {
    if (initialStoredFilters?.batchId == null) {
      return;
    }

    if (!batchManagement.selectedBatchStatus) {
      return;
    }

    batchManagement.setSelectedBatchId(initialStoredFilters.batchId);
  }, [initialStoredFilters?.batchId, batchManagement.selectedBatchStatus]);

  useEffect(() => {
    if (!schoolCandidateNewResidences) {
      return;
    }

    if (!!schoolCandidateNewResidences.results.length) {
      const pagedResult: PagedResult<
        SchoolCandidateResidenceChange & Selectable
      > = {
        ...schoolCandidateNewResidences,
        results: ObjectUtils.makeSelectableArray(
          schoolCandidateNewResidences.results
        ),
      };

      setTableData(pagedResult);

      return;
    }

    setTableData(defaultTableData);
  }, [schoolCandidateNewResidences]);

  useEffect(() => {
    if (!currentSchoolYear?.id) {
      return;
    }

    evaluateSchoolYearEditable(currentSchoolYear.id);
  }, [currentSchoolYear, evaluateSchoolYearEditable]);

  const handleFiltersClear = () => {
    setTableData(defaultTableData);
    assignCardRef.current?.resetAssignmentState();
    setCurrentSearchRequest(null);
  };

  const handleCountyChange = (countyId?: number) => {
    setCurrentCountyId(countyId);

    assignCardRef.current?.handleCountyChange(countyId);
  };

  const handleStartAddressPickup = async (
    countyId?: number
  ): Promise<SaveResult<number>> => {
    if (!countyId || !isSchoolYearEditable) {
      return {
        isValid: false,
        validationFailures: [
          {
            propertyName: null,
            message: "Županija ili školska godina nije odabrana.",
          },
        ],
      };
    }

    try {
      return await startSchoolCandidateBatchRequest(countyId).unwrap();
    } catch {
      return {
        isValid: false,
        validationFailures: [
          {
            propertyName: null,
            message: "Došlo je do greške pri pokretanju preuzimanja adresa.",
          },
        ],
      };
    }
  };

  const handleDetailsClicked = (id?: number) => {
    dispatch(setRedirectURL(history.location.pathname));
    if (id) {
      if (currentFilters) {
        saveState(currentFilters, tableState);
      }

      history.push(schoolCandidateDetailUrl(id!));
    }
  };

  const handleAssignCardSearch = useCallback(() => {
    if (!currentSearchRequest || !selectedBatchId || !currentCountyId) {
      return;
    }

    const nextRequest: SchoolCandidateNewResidencesRequest = {
      ...currentSearchRequest,
      countyId: currentCountyId,
      batchId: selectedBatchId,
      search: {
        ...currentSearchRequest.search,
        page: Math.max((tableState.page ?? 1) - 1, 0),
        pageSize: tableState.pageSize ?? 10,
      },
    };

    setCurrentSearchRequest(nextRequest);
    handleSearchNewResidences(nextRequest);
  }, [
    selectedBatchId,
    currentCountyId,
    currentSearchRequest,
    handleSearchNewResidences,
    tableState.page,
    tableState.pageSize,
  ]);

  const handleSaveFilters = (filters: SchoolCandidateResidenceChangeSearch) => {
    saveState(
      {
        ...filters,
        countyId: currentCountyId,
        batchId: batchManagement.selectedBatchId ?? null,
      },
      tableState
    );
  };

  if (!schoolYears || !currentSchoolYear || !authCounties)
    return <Spinner show={true} />;

  function handleSelectedSchoolYear(schoolYearId: number): void {
    setSelectedSchoolYear(schoolYearId);
  }

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("navigation.schoolCandidateList")}
        errors={errors}
      />
      <SchoolCandidateAddressAccessFilters
        authCounties={authCounties}
        initialStoredFilters={initialStoredFilters}
        institutions={institutions}
        schoolYears={schoolYears}
        currentSchoolYear={currentSchoolYear}
        storageKey={storageKey}
        onClear={handleFiltersClear}
        onCountyChange={handleCountyChange}
        onSaveFilters={handleSaveFilters}
        onSearchNewResidences={handleSearchNewResidences}
        tablePage={tableState.page ?? 1}
        tablePageSize={tableState.pageSize ?? 10}
        batchManagement={batchManagement}
        countyId={currentCountyId}
        onStartAddressPickup={handleStartAddressPickup}
        onSendSelectedSchoolYear={handleSelectedSchoolYear}
      />
      <SchoolCandidateAssignCard
        ref={assignCardRef}
        countyId={currentCountyId}
        batchId={selectedBatchId}
        institutions={institutions}
        isSchoolYearEditable={isSchoolYearEditable}
        currentSchoolYear={currentSchoolYear}
        selectedSchoolYearId={selectedSchoolYear ?? 0}
        onSearch={handleAssignCardSearch}
        saveInstitutionAssignmentRequest={saveInstitutionAssignmentRequest}
        setErrors={setErrors}
        setInstitutionAssignmentRequest={setInstitutionAssignmentRequest}
        setTableData={setTableData}
        tableData={tableData}
        downloadAddressesRequest={downloadSchoolCandidateResidencesRequest}
      />
      {isSchoolCandidateNewResidencesFetching && <Spinner show={true} />}
      <ListTable
        tableProps={tableProps}
        pageSizeOptions={[50, 100, 200, 300, 500, 1000, 1500, 2000, 6000]}
      />
    </ContentArea>
  );
}
